import { ChatRoomMessageChannel, ChatRoomType } from '@commenda/prisma';
import { AppType } from '../useAppContext';
import {
  IChannelReadState,
  IChatRoomMessage,
  ICorporationUnreadChatRoomMessage,
  IUnreadChatRoomMessage,
  UserTypes,
} from '@commenda/types/src/chat-room/types';
import { timeAgo } from '../datetime';

type ChatRoomWithChannels = {
  channels?: IChannelReadState[];
  unreadMessagesCount?: number;
  internalNotesUnreadCount?: number;
};

export type ChatRoomParentTab = 'CUSTOMER' | 'INTERNAL' | 'CUSTOMER_INTERNAL';

export class ChatRoomModel {
  /** Total unread across all channels. Prefers channels[] when present. */
  static getTotalUnread(room: ChatRoomWithChannels): number {
    if (room.channels?.length) {
      return room.channels.reduce((sum, c) => sum + c.unreadMessagesCount, 0);
    }
    return (room.unreadMessagesCount ?? 0) + (room.internalNotesUnreadCount ?? 0);
  }

  /** Unread count for a specific channel. Prefers channels[] when present. */
  static getChannelUnread(
    room: ChatRoomWithChannels,
    channel: ChatRoomMessageChannel
  ): number {
    const state = room.channels?.find((c) => c.channel === channel);
    if (state != null) return state.unreadMessagesCount;
    if (channel === 'CUSTOMER') return room.unreadMessagesCount ?? 0;
    if (channel === 'AGENT_INTERNAL') return room.internalNotesUnreadCount ?? 0;
    return 0;
  }

  /**
   * Determines which channel should be marked as read based on the current
   * user type, layout configuration, and active tab.
   */
  static resolveChannelForRead(opts: {
    isAgent: boolean;
    hideInternalNotes: boolean;
    includeCustomerInternalNotes: boolean;
    activeParentTab: ChatRoomParentTab;
  }): ChatRoomMessageChannel {
    const { isAgent, hideInternalNotes, includeCustomerInternalNotes, activeParentTab } = opts;
    if (isAgent) {
      if (!hideInternalNotes) {
        return activeParentTab === 'INTERNAL' ? 'AGENT_INTERNAL' : 'CUSTOMER';
      }
      return includeCustomerInternalNotes && activeParentTab === 'CUSTOMER_INTERNAL'
        ? 'CUSTOMER_INTERNAL'
        : 'CUSTOMER';
    }
    return activeParentTab === 'CUSTOMER_INTERNAL' ? 'CUSTOMER_INTERNAL' : 'CUSTOMER';
  }

  /** Whether a room has any unread messages from the viewer's perspective. */
  static hasUnread(room: ChatRoomWithChannels, includeInternalNotes: boolean): boolean {
    const count = includeInternalNotes
      ? ChatRoomModel.getTotalUnread(room)
      : ChatRoomModel.getChannelUnread(room, 'CUSTOMER');
    return count > 0;
  }

  /** Returns true if a message is an agent-internal note. */
  static isInternalNote(message: Pick<IChatRoomMessage, 'isInternalNoteMessage' | 'channel'>): boolean {
    return (
      message.channel === 'AGENT_INTERNAL' ||
      (message.channel == null && !!message.isInternalNoteMessage)
    );
  }

  /** Filters out agent-internal messages — the set visible to customers. */
  static filterCustomerVisible(messages: IChatRoomMessage[]): IChatRoomMessage[] {
    return messages.filter((m) => !ChatRoomModel.isInternalNote(m));
  }

  static canSeeAgentInternalRoom(appType: AppType): boolean {
    return appType === AppType.AGENT || appType === AppType.ADMIN;
  }

  /**
   * Resolves the channel (and legacy isInternalNoteMessage flag) to attach
   * when sending a message, based on the current tab/variant context.
   */
  static resolveSendChannel(opts: {
    isAgent: boolean;
    activeParentTab: ChatRoomParentTab;
    includeCustomerInternalNotes: boolean;
  }): { channel: ChatRoomMessageChannel | undefined; isInternalNoteMessage: boolean } {
    const { isAgent, activeParentTab, includeCustomerInternalNotes } = opts;
    if (includeCustomerInternalNotes && activeParentTab === 'CUSTOMER_INTERNAL') {
      return { channel: 'CUSTOMER_INTERNAL', isInternalNoteMessage: false };
    }
    if (isAgent && activeParentTab === 'INTERNAL') {
      return { channel: 'AGENT_INTERNAL', isInternalNoteMessage: true };
    }
    return { channel: undefined, isInternalNoteMessage: false };
  }

  /**
   * Maps raw API unread messages to the normalised `IUnreadChatRoomMessage`
   * shape used by notification components.
   */
  static parseUnreadMessages(
    messages: ICorporationUnreadChatRoomMessage[]
  ): IUnreadChatRoomMessage[] {
    return (messages ?? []).map((message) => ({
      corporationName: message.corporationName,
      filingName: message.title,
      activityType: 'Sent a message',
      messageId: message.message.id,
      corporationId: message.corporationId,
      complianceId: message.complianceId,
      serviceRequestId: message.serviceRequestId,
      chatRoomId: message.chatRoomId,
      chatRoomType: message.chatRoomType,
      createdBy: message.message.sender.name,
      createdAt: timeAgo(message.message.sentAt),
    }));
  }

  /**
   * A SERVICE_REQUEST_THREAD message without a serviceRequestId is not
   * navigable and should not be displayed in notification lists.
   */
  static isValidForDisplay(message: IUnreadChatRoomMessage): boolean {
    if (message.chatRoomType === ChatRoomType.SERVICE_REQUEST_THREAD) {
      return !!message.serviceRequestId;
    }
    // GENERAL_THREAD messages are always valid for display
    if (message.chatRoomType === "GENERAL_THREAD") {
      return true;
    }
    return true;
  }

  /** Stable DOM element ID for a chat room trigger button. */
  static getButtonId(chatRoomId: string | undefined): string {
    return `chatroom_${chatRoomId}`;
  }

  static getChatRoomThreadUrl = (
    message: IUnreadChatRoomMessage,
    userType: UserTypes
  ) => {
    let url = '';
    if (message.chatRoomType === ChatRoomType.COMPLIANCE_THREAD)
      if (userType === UserTypes.ACCOUNTANT) {
        url = `/operations/compliance/${message.complianceId}`;
      } else {
        url = `/compliance-monitor?corporationId=${message.corporationId}&selectedFiling=${message.complianceId}`;
      }
    else if (message.chatRoomType === ChatRoomType.BOOKKEEPING_THREAD) {
      url = `${window.location.origin}/corporation/${message.corporationId}?activeTab=bookkeeping`;
      window.location.href = url;
    } else if (
      message.chatRoomType === ChatRoomType.MARKETPLACE_SERVICE_THREAD
    ) {
      url = '/marketplace-tasks';
    } else if (message.chatRoomType === ChatRoomType.SERVICE_REQUEST_THREAD) {
      url = `/service-request/${message.serviceRequestId}`;
    } else if (message.chatRoomType === "GENERAL_THREAD") {
      url = `/general-inbox/${message.chatRoomId}`;
    }
    return url;
  };
}
